// src/contexts/AuthContext.tsx
import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePostHog } from 'posthog-js/react';
import {
  loginUser,
  registerUser,
  logoutUser,
  refreshUserToken,
  getUserProfile,
  getTokenExpiry,
  isTokenExpired,
} from "../api/auth";
import { initializeUrlProviders, resetUrlProviders } from "../services/websocket/urlProviders";
import { setupAuthInterceptor } from "../api/interceptors";
import { usePostHogAnalytics } from '../hooks/usePostHogAnalytics';
import { PostHogEvents } from '../utils/posthogEvents';

// Create the Auth Context
export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType
);

// Token expiration buffer (5 minutes in milliseconds)
const TOKEN_EXPIRATION_BUFFER = 5 * 60 * 1000;

// Inactivity timeout (1 hour in milliseconds)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000;

export interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  birthday?: string | null;
  isVerified: boolean;
  avatar?: string;
  tagline?: string;
  profile_exists?: boolean;
  is_superuser?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: () => boolean;
  isRegistering: boolean;
  login: (
    usernameOrEmail: string,
    password: string
  ) => Promise<{ success: boolean; profileExists: boolean | null }>;
  register: (
    email: string,
    username: string,
    password: string
  ) => Promise<{ success: boolean; autoLoggedIn?: boolean }>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  fetchUserProfile: () => Promise<User | null>;
  getAuthHeaders: () => Record<string, string>;
  clearAuthError: () => void;
  markProfileAsCompleted: (updatedUserFromApi: User) => void;
  clearRegistrationFlag: () => void;
  trackEmailVerification: (userId: string) => void;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Core state
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  );
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const [profileUpdateInProgress, setProfileUpdateInProgress] = useState(false);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [isRegistering, setIsRegistering] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  
  // PostHog analytics hooks
  const posthog = usePostHog();
  const { trackAuth, identifyUser } = usePostHogAnalytics();
  
  // Debug logging
  useEffect(() => {
    console.log('🔍 AuthContext PostHog Debug:', {
      posthogInstance: posthog,
      isPostHogDefined: !!posthog,
      trackAuthFunction: trackAuth,
      identifyUserFunction: identifyUser,
      posthogType: typeof posthog
    });
  }, [posthog]);

  // Get token expiration time
  const parseToken = useCallback((token: string) => {
    return getTokenExpiry(token);
  }, []);

  // Set token and update localStorage with EXTENSIVE debugging
  const setAuthToken = useCallback(
    (newToken: string | null) => {
      const debugId = `AUTH-TOKEN-${Date.now()}`;
      console.log(`🔐 [${debugId}] ========== SETTING AUTH TOKEN ==========`);
      
      if (newToken) {
        console.log(`📝 [${debugId}] New token received:`, {
          tokenPreview: newToken.substring(0, 50) + "...",
          tokenLength: newToken.length,
          tokenType: newToken.startsWith('Bearer ') ? 'Has Bearer prefix' : 'No Bearer prefix'
        });
        
        // Decode token for debugging
        try {
          const tokenParts = newToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log(`🔍 [${debugId}] Token payload decoded:`, {
              userId: payload.sub,
              issuedAt: new Date(payload.iat * 1000).toISOString(),
              expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'No expiry',
              isExpired: payload.exp ? Date.now() > payload.exp * 1000 : false,
              hoursUntilExpiry: payload.exp ? ((payload.exp * 1000 - Date.now()) / (1000 * 60 * 60)).toFixed(2) : 'N/A'
            });
          }
        } catch (e) {
          console.warn(`⚠️ [${debugId}] Could not decode token:`, e.message);
        }
        
        localStorage.setItem("token", newToken);
        setToken(newToken);
        console.log(`✅ [${debugId}] Token stored in localStorage and state`);

        // Parse and set expiry time
        const expiryTime = parseToken(newToken);
        if (expiryTime) {
          setTokenExpiry(expiryTime);
          console.log(`⏰ [${debugId}] Token expiry set to:`, new Date(expiryTime).toISOString());
        } else {
          console.warn(`⚠️ [${debugId}] Could not parse token expiry time`);
        }
        
        // Initialize WebSocket URL providers with new token
        initializeUrlProviders(newToken);
      } else {
        console.log(`🗑️ [${debugId}] Clearing token from storage`);
        localStorage.removeItem("token");
        setToken(null);
        setTokenExpiry(null);
        
        // Reset WebSocket URL providers on logout
        resetUrlProviders();
      }
      
      console.log(`🔐 [${debugId}] ========== TOKEN UPDATE COMPLETE ==========`);
    },
    [parseToken]
  );

  // Log out a user
  const logout = useCallback(async () => {
    if (!token) return;

    // Track logout event before clearing state
    trackAuth(PostHogEvents.USER_LOGGED_OUT, {
      user_journey_stage: 'session_end',
      logout_method: 'user_initiated'
    });
    
    // Reset PostHog identity
    posthog?.reset();

    // Clear state and storage immediately to prevent race conditions
    setAuthToken(null);
    localStorage.removeItem("user");
    setUser(null);
    
    // Navigate to login page immediately after clearing state
    navigate("/login");
    
    // Then try to call logout API to blacklist the token (non-blocking)
    try {
      await logoutUser(token);
    } catch (error) {
      console.error("Error during logout API call:", error);
      // Don't throw - user is already logged out locally
    }
  }, [token, setAuthToken, navigate, trackAuth, posthog]);

  // Refresh token before expiration
  const refreshToken = useCallback(async () => {
    if (!token) return false;

    try {
      const data = await refreshUserToken(token);
      const newToken = data.access_token;

      if (!newToken) {
        throw new Error("No token received from refresh endpoint");
      }

      // Update token and expiry
      setAuthToken(newToken);

      // Update user with new token if user exists
      if (user) {
        const updatedUser = { ...user };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

      // Track successful token refresh
      trackAuth(PostHogEvents.TOKEN_REFRESHED, {
        user_journey_stage: 'session_extended',
        refresh_method: 'automatic'
      });

      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Don't logout here to allow retry
      return false;
    }
  }, [token, user, setAuthToken]);

  // Fetch user profile from API
  const fetchUserProfile = useCallback(
    async (bypassCache: boolean = false, tokenOverride?: string) => {
    const authToken = tokenOverride || token;
    if (!authToken) return null;

    try {
      const response = await getUserProfile(authToken, bypassCache);
      const userData = response.user || response;

      console.log("User profile API response:", response);
      console.log("Extracted user data for AuthContext:", userData);

      const userProfile: User = {
        id: userData.user_id || userData.id,
        username: userData.username,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        birthday: userData.birthday,
        isVerified: userData.email_verified !== undefined ? userData.email_verified : false,
        avatar: userData.avatar || userData.profile?.profile_settings?.avatar_url,
        tagline: userData.profile?.profile_settings?.tagline,
        profile_exists: userData.profile_exists,
        is_superuser: userData.is_superuser,
      };

      localStorage.setItem("user", JSON.stringify(userProfile));
      setUser(userProfile);
      console.log("User state updated with profile_exists:", userProfile.profile_exists);
      return userProfile;
    } catch (error) {
      console.error("Error fetching user profile:", error);

      // Handle unauthorized errors
      if (error.message && error.message.includes("401")) {
        setAuthToken(null);
        setUser(null);
      }

      return null;
    }
  }, [token, setAuthToken]);

  // Login function
  const login = async (usernameOrEmail: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const loginResponse = await loginUser(usernameOrEmail, password);
      const newToken = loginResponse.access_token;
      if (!newToken) {
        throw new Error("No access token received");
      }
      setAuthToken(newToken);
      
      // Track login immediately with basic info (before profile fetch)
      console.log('🎯 Tracking USER_LOGGED_IN immediately after successful login');
      trackAuth(PostHogEvents.USER_LOGGED_IN, {
        login_method: 'email',
        username_or_email: usernameOrEmail,
        login_timestamp: new Date().toISOString(),
        has_token: true
      });

      // Fetch user profile which now includes profile_exists and email verification status
      // Pass newToken directly to avoid race condition with state update
      const fetchedUser = await fetchUserProfile(false, newToken);
      
      // Check email verification first
      if (fetchedUser && fetchedUser.isVerified === false) {
        console.log(
          "User email not verified, navigating to /verify-email"
        );
        navigate("/verify-email", { 
          state: { email: fetchedUser.email } 
        });
        return { success: true, profileExists: false, emailVerified: false };
      }
      
      // Then check profile completion only if email is verified
      const currentProfileExists = fetchedUser?.profile_exists ?? loginResponse.profile_exists;

      // Track detailed user info if profile fetched successfully
      if (fetchedUser) {
        // Check if this is a returning user
        const lastLoginDate = localStorage.getItem('last_login_date');
        const isReturningUser = !!lastLoginDate;
        const currentDate = new Date().toISOString();
        
        // Calculate days since last login if returning
        let daysSinceLastLogin = null;
        if (lastLoginDate) {
          const lastDate = new Date(lastLoginDate);
          const currentDateObj = new Date();
          daysSinceLastLogin = Math.floor((currentDateObj.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
        }
        
        // Track more detailed login with user profile info
        console.log('📊 Tracking detailed user info after profile fetch');
        trackAuth('user_login_with_profile', {
          login_method: 'email',
          user_journey_stage: isReturningUser ? 'returning' : 'new',
          email_verified: fetchedUser.isVerified,
          profile_exists: currentProfileExists,
          is_returning_user: isReturningUser,
          days_since_last_login: daysSinceLastLogin,
          login_timestamp: currentDate,
          user_id: fetchedUser.id,
          username: fetchedUser.username,
          email: fetchedUser.email
        });
        
        // Update last login date
        localStorage.setItem('last_login_date', currentDate);
        
        // Get or set first login date for cohort analysis
        let firstLoginDate = localStorage.getItem('first_login_date');
        if (!firstLoginDate) {
          firstLoginDate = currentDate;
          localStorage.setItem('first_login_date', firstLoginDate);
        }
        
        // Calculate days since first login
        const daysSinceFirstLogin = Math.floor((new Date().getTime() - new Date(firstLoginDate).getTime()) / (1000 * 60 * 60 * 24));
        
        // Identify user in PostHog with enhanced properties
        identifyUser(fetchedUser.id.toString(), {
          email: fetchedUser.email,
          username: fetchedUser.username,
          email_verified: fetchedUser.isVerified,
          profile_exists: currentProfileExists,
          is_superuser: fetchedUser.is_superuser || false,
          first_login_date: firstLoginDate,
          last_login_date: currentDate,
          days_since_first_login: daysSinceFirstLogin,
          login_count: parseInt(localStorage.getItem('login_count') || '0') + 1
        });
        
        // Update login count
        const loginCount = parseInt(localStorage.getItem('login_count') || '0') + 1;
        localStorage.setItem('login_count', loginCount.toString());
        
        // Track retention milestones
        if (daysSinceFirstLogin === 1) {
          trackAuth('user_retained_day_1', {
            retention_milestone: 'day_1',
            user_id: fetchedUser.id.toString()
          });
        } else if (daysSinceFirstLogin === 7) {
          trackAuth('user_retained_day_7', {
            retention_milestone: 'day_7',
            user_id: fetchedUser.id.toString()
          });
        } else if (daysSinceFirstLogin === 30) {
          trackAuth('user_retained_day_30', {
            retention_milestone: 'day_30',
            user_id: fetchedUser.id.toString()
          });
        }
      }

      if (currentProfileExists === false) {
        console.log(
          "User profile does not exist, navigating to /onboarding"
        );
        navigate("/onboarding");
        return { success: true, profileExists: false, emailVerified: true };
      }

      return { success: true, profileExists: true, emailVerified: true };
    } catch (error) {
      // Track login failure
      trackAuth(PostHogEvents.USER_LOGIN_FAILED, {
        login_method: 'email',
        error_message: error.message,
        user_journey_stage: 'authentication_failed'
      });
      
      setError(error.message);
      return { success: false, profileExists: null };
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (
    email: string,
    username: string,
    password: string
  ) => {
    console.log("🔥 AUTH: Starting registration process");
    setIsLoading(true);
    setError(null);
    setIsRegistering(true); // Set flag to prevent auto-navigation
    console.log("🔥 AUTH: Set isLoading=true, isRegistering=true");

    try {
      // Call register API - now returns email verification response
      const registrationResult = await registerUser(email, username, password);
      
      console.log("Registration successful:", registrationResult);
      
      // Auto-login after successful registration
      // But still show email verification page - they can skip if they want
      let autoLoggedIn = false;
      try {
        const loginData = await loginUser(username, password);
        console.log("Auto-login after registration successful");
        
        // Set the token using setAuthToken for proper handling
        const newToken = loginData.access_token || loginData.token?.access_token;
        if (newToken) {
          setAuthToken(newToken);
        }
        
        // Fetch user profile after login
        const newUser = await fetchUserProfile();
        
        // Track successful registration and auto-login
        if (newUser) {
          const signupDate = new Date().toISOString();
          
          trackAuth(PostHogEvents.USER_SIGNED_UP, {
            signup_method: 'email',
            user_journey_stage: 'new',
            auto_logged_in: true,
            signup_timestamp: signupDate
          });
          
          // Set initial tracking data for new user
          localStorage.setItem('first_login_date', signupDate);
          localStorage.setItem('last_login_date', signupDate);
          localStorage.setItem('login_count', '1');
          localStorage.setItem('user_signup_date', signupDate);
          
          // Identify new user in PostHog with comprehensive properties
          identifyUser(newUser.id.toString(), {
            email: newUser.email,
            username: newUser.username,
            created_at: signupDate,
            signup_date: signupDate,
            first_login_date: signupDate,
            last_login_date: signupDate,
            signup_source: 'organic',
            email_verified: newUser.isVerified,
            profile_exists: newUser.profile_exists,
            login_count: 1,
            days_since_signup: 0,
            is_new_user: true
          });
          
          // Track initial activation event
          trackAuth('user_activated', {
            activation_type: 'completed_signup',
            user_id: newUser.id.toString(),
            activation_timestamp: signupDate
          });
        }
        
        autoLoggedIn = true;
      } catch (loginError) {
        console.log("Auto-login failed, but registration successful:", loginError);
        // Even if auto-login fails, registration was successful
        autoLoggedIn = false;
      }

      // Navigate to email verification after all registration steps complete
      console.log("🔥 AUTH: Navigating to email verification");
      navigate('/verify-email', { 
        state: { email } 
      });
      
      return { success: true, autoLoggedIn };
    } catch (error: any) {
      // If registerUser throws (e.g., username taken), it will be caught here.
      console.error("🔥 AUTH: Registration error:", error);
      
      // Track registration failure
      trackAuth(PostHogEvents.USER_SIGNUP_FAILED, {
        signup_method: 'email',
        error_message: error.message,
        user_journey_stage: 'registration_failed'
      });
      
      setError(error.message);
      return { success: false };
    } finally {
      console.log("🔥 AUTH: Setting isLoading=false");
      setIsLoading(false);
      // Don't clear isRegistering here - it will be cleared when user navigates away from email verification
    }
  };

  // Check if token is valid
  const isTokenValid = useCallback(() => {
    if (!token) {
      // Also check localStorage in case state is out of sync
      const storedToken = localStorage.getItem("token");
      if (!storedToken) {
        return false;
      }
      // Check if stored token is valid
      return !isTokenExpired(storedToken);
    }
    return !isTokenExpired(token);
  }, [token]);

  // Get authentication headers for API requests with EXTENSIVE debugging
  const getAuthHeaders = useCallback(() => {
    const debugId = `GET-AUTH-HEADERS-${Date.now()}`;
    console.log(`🎫 [${debugId}] ========== GETTING AUTH HEADERS ==========`);
    
    let authToken = token;
    console.log(`📌 [${debugId}] Token from state:`, {
      hasStateToken: !!token,
      stateTokenPreview: token ? token.substring(0, 30) + '...' : 'NULL'
    });
    
    // Fallback to localStorage if state token is missing
    if (!authToken) {
      authToken = localStorage.getItem("token");
      console.log(`💾 [${debugId}] Falling back to localStorage:`, {
        hasLocalStorageToken: !!authToken,
        localStorageTokenPreview: authToken ? authToken.substring(0, 30) + '...' : 'NULL'
      });
    }
    
    if (!authToken) {
      console.warn(`⚠️ [${debugId}] NO TOKEN AVAILABLE - returning empty headers`);
      return {};
    }
    
    // Validate token before returning
    try {
      const tokenParts = authToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const isExpired = payload.exp ? Date.now() > payload.exp * 1000 : false;
        console.log(`🔍 [${debugId}] Token validation:`, {
          userId: payload.sub,
          isExpired: isExpired,
          expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'No expiry',
          minutesUntilExpiry: payload.exp ? Math.floor((payload.exp * 1000 - Date.now()) / (1000 * 60)) : 'N/A'
        });
        
        if (isExpired) {
          console.error(`❌ [${debugId}] TOKEN IS EXPIRED! This will likely cause authentication to fail`);
        }
      }
    } catch (e) {
      console.warn(`⚠️ [${debugId}] Could not validate token format:`, e.message);
    }
    
    const headers = {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    };
    
    console.log(`✅ [${debugId}] Returning headers:`, {
      hasAuthorization: !!headers.Authorization,
      authorizationPreview: headers.Authorization.substring(0, 50) + '...',
      contentType: headers["Content-Type"]
    });
    
    return headers;
  }, [token]);

  // Setup auth interceptor on mount
  useEffect(() => {
    setupAuthInterceptor(logout);
  }, [logout]);

  // Initialize auth state from localStorage
  useEffect(() => {
    let needsFetch = false;
    
    const initialize = async () => {
      // Only set loading if we need to fetch user profile
      const storedToken = localStorage.getItem("token");
      needsFetch = storedToken && !localStorage.getItem("user");
      
      if (needsFetch) {
        setIsLoading(true);
      }
      
      try {
        if (storedToken) {
          setToken(storedToken);
          const expiryTime = parseToken(storedToken);
          setTokenExpiry(expiryTime);
          
          // Initialize WebSocket URL providers on app startup
          initializeUrlProviders(storedToken);

          if (expiryTime && Date.now() < expiryTime) {
            const storedUserJSON = localStorage.getItem("user"); // Renamed for clarity
            if (storedUserJSON) {
              try {
                // Parse stored user, it might not have profile_exists yet if from an old session
                const parsedUser = JSON.parse(storedUserJSON) as User;
                setUser(parsedUser);
                
                // Check email verification and profile status from cached user
                const excludedPaths = ["/onboarding", "/verify-email"];
                const shouldExclude = excludedPaths.some(path => location.pathname.startsWith(path));
                
                if (!profileUpdateInProgress && parsedUser && !shouldExclude && !isRegistering) {
                  // Check email verification first
                  if (parsedUser.isVerified === false) {
                    console.log("Initialize (cached): User email not verified, navigating to /verify-email");
                    navigate("/verify-email", { 
                      state: { email: parsedUser.email } 
                    });
                  }
                  // Then check profile completion only if email is verified
                  else if (parsedUser.isVerified && parsedUser.profile_exists === false) {
                    console.log("Initialize (cached): User profile incomplete, navigating to /onboarding");
                    navigate("/onboarding");
                  }
                }
              } catch (error) {
                console.error("Error parsing stored user data:", error);
                localStorage.removeItem("user");
              }
            }

            // Only fetch if we need fresh data
            let freshUser = null;
            if (needsFetch) {
              // Fetch fresh user data which includes profile_exists
              freshUser = await fetchUserProfile();
            } else {
              // Fetch in background without blocking
              fetchUserProfile().catch(error => {
                console.error("Background profile fetch failed:", error);
              });
            }

            // Check for email verification and profile completion after fetching fresh user data
            // Don't redirect if user is on email verification page or onboarding
            const excludedPaths = ["/onboarding", "/verify-email"];
            const shouldExclude = excludedPaths.some(path => location.pathname.startsWith(path));
            
            // Check email verification first
            if (!profileUpdateInProgress && freshUser && freshUser.isVerified === false && !shouldExclude && !isRegistering) {
              console.log("Initialize: User email not verified, navigating to /verify-email");
              navigate("/verify-email", { 
                state: { email: freshUser.email } 
              });
            }
            // Then check profile completion only if email is verified
            else if (!profileUpdateInProgress && freshUser && freshUser.isVerified && freshUser.profile_exists === false && !shouldExclude && !isRegistering) {
              console.log("Initialize: User profile does not exist (after fetch), navigating to /onboarding");
              navigate("/onboarding");
            }
            
          } else {
            setAuthToken(null);
            setUser(null); // Clear user if token is expired
            localStorage.removeItem("user"); // Also clear from storage
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        // Potentially logout or clear token if init fails critically
        setAuthToken(null);
        setUser(null);
        localStorage.removeItem("user");
      } finally {
        // Always set loading false when initialization completes
        setIsLoading(false);
      }
    };

    initialize();
  }, [parseToken, fetchUserProfile, setAuthToken, navigate, location.pathname, profileUpdateInProgress, isRegistering]);

  // Set up token refresh
  useEffect(() => {
    if (!tokenExpiry || !token) return;

    const now = Date.now();
    const timeUntilExpiry = tokenExpiry - now;

    // If token is about to expire, refresh it immediately
    if (timeUntilExpiry < TOKEN_EXPIRATION_BUFFER) {
      refreshToken();
      return;
    }

    // Otherwise, set up a timer to refresh before expiration
    const refreshTimerId = setTimeout(() => {
      refreshToken();
    }, timeUntilExpiry - TOKEN_EXPIRATION_BUFFER);

    return () => clearTimeout(refreshTimerId);
  }, [tokenExpiry, token, refreshToken]);

  // Check if user is authenticated (for protected routes)
  const isAuthenticated = useCallback(() => {
    // Only check if token is valid - user profile might still be loading
    // This prevents race conditions where user is temporarily null during profile fetch
    return isTokenValid();
  }, [isTokenValid]);

  // Add this new useEffect for ongoing profile checks
  useEffect(() => {
    // Do not run this check if a profile update was just optimistically set
    if (profileUpdateInProgress) {
      console.log("Ongoing check: Skipped due to profileUpdateInProgress.");
      return;
    }
    // Don't redirect if user is on email verification page or onboarding
    const excludedPaths = ["/onboarding", "/verify-email"];
    const shouldExclude = excludedPaths.some(path => location.pathname.startsWith(path));
    
    if (user && user.profile_exists === false && isAuthenticated() && !shouldExclude && !isLoading && !isRegistering) {
      console.log("Ongoing check: User profile incomplete, navigating to /onboarding. Current path:", location.pathname);
      navigate("/onboarding");
    }
  }, [user, isAuthenticated, location.pathname, navigate, isLoading, profileUpdateInProgress, isRegistering]);

  // Clear registration flag (called when user navigates away from email verification)
  const clearRegistrationFlag = useCallback(() => {
    setIsRegistering(false);
  }, []);

  // Track email verification completion (to be called from verification page)
  const trackEmailVerification = useCallback((userId: string) => {
    trackAuth(PostHogEvents.USER_EMAIL_VERIFIED, {
      user_journey_stage: 'onboarding',
      verification_method: 'email_link'
    });
  }, [trackAuth]);

  // Function to explicitly mark profile as completed
  const markProfileAsCompleted = useCallback((updatedUserFromApi: User) => {
    setProfileUpdateInProgress(true); 
    setUser(currentUser => {
      // Merge with existing currentUser if some fields are not in updatedUserFromApi,
      // but updatedUserFromApi should be the source of truth for profile fields.
      const fullyUpdatedUser = { 
        ...currentUser, 
        ...updatedUserFromApi, 
        profile_exists: true // Explicitly ensure this is true, though API should send it
      };
      localStorage.setItem("user", JSON.stringify(fullyUpdatedUser));
      console.log("markProfileAsCompleted: User state and localStorage updated with full user object from API", fullyUpdatedUser);
      return fullyUpdatedUser;
    });
  }, []);

  // Effect to reset profileUpdateInProgress after navigation away from onboarding
  useEffect(() => {
    if (profileUpdateInProgress && !location.pathname.startsWith("/onboarding")) {
      console.log("profileUpdateInProgress: Navigated away from /onboarding, resetting flag soon.");
      const timer = setTimeout(() => {
        setProfileUpdateInProgress(false);
        console.log("profileUpdateInProgress: Flag reset.");
      }, 500); // Adjust delay as needed, 500ms gives some buffer
      return () => clearTimeout(timer);
    }
  }, [location.pathname, profileUpdateInProgress]);

  // Clear auth error
  const clearAuthError = useCallback(() => {
    setError(null);
  }, []);

  // Track user activity
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Set up activity tracking
  useEffect(() => {
    const handleActivity = () => {
      updateActivity();
    };

    // Track user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [updateActivity]);

  // Check for inactivity timeout
  useEffect(() => {
    if (!isAuthenticated()) return;

    const checkInactivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      
      // Warn user 5 minutes before inactivity timeout
      if (timeSinceLastActivity > INACTIVITY_TIMEOUT - 5 * 60 * 1000 && 
          timeSinceLastActivity < INACTIVITY_TIMEOUT - 4 * 60 * 1000) {
        console.log('Warning: Session will expire in 5 minutes due to inactivity');
        // You could trigger a toast/modal here to warn the user
      }
      
      if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
        console.log('Session expired due to inactivity');
        setError('Your session has expired due to inactivity. Please log in again.');
        logout();
      }
    };

    // Check every minute
    const intervalId = setInterval(checkInactivity, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [lastActivity, isAuthenticated, logout]);

  // Context value
  const contextValue: AuthContextType = {
    user,
    token,
    isLoading,
    error,
    isAuthenticated,
    isRegistering,
    login,
    register,
    logout,
    refreshToken,
    fetchUserProfile,
    getAuthHeaders,
    clearAuthError,
    markProfileAsCompleted,
    clearRegistrationFlag,
    trackEmailVerification,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
